export const OPTIMAL_CARE_BRAND = "optimalcaregrp";

export const BRAND_HOSTNAMES = {
  rpg: ["rpg", "ruralphysiciansgroup", "compassai"],
  healcore: ["healcore"],
  shoreline: ["shoreline"],
  gtm: ["gtm", "totalsurgical"],
  [OPTIMAL_CARE_BRAND]: [OPTIMAL_CARE_BRAND],
  freehealthai: ["freehealthai"],
  orders: ["orders"],
  // alpha-orders' provider tenant runs on its OWN brain (alphabiologix.lucashealth.ai),
  // distinct from the shared 'orders' brain (staging.lucashealth.ai). Registered so
  // provider invite links carry &brand=alphabiologix and the invitee's CreateAccount
  // resolves isOrdersBrand → order-provider (not the default free-trial account).
  alphabiologix: ["alphabiologix"],
};

// The hostname-allowlist keys are the single source of truth for valid brands.
// (Note: NOT the termsOfUseVersion keys — those include "default", which must
// never be accepted as a real brand override.)
export const KNOWN_BRANDS = Object.keys(BRAND_HOSTNAMES);

// A brand supplied via ?brand= or restored from sessionStorage is honored ONLY
// when it's a recognized brand. Case-insensitive so "RPG"/"Orders" still match.
// Any other value (an org slug, a typo, a stale junk value) is rejected.
//
// Why this matters: a junk brand poisons every brand-keyed lookup. The worst
// is the Terms-of-Use version map — an unknown key resolves to `undefined`,
// which JSON.stringify silently drops from the profile save, so the signed ToS
// version never reaches the server and the onboarding wizard loops forever.
export function isKnownBrand(brand) {
  return typeof brand === "string" && KNOWN_BRANDS.includes(brand.toLowerCase());
}

export function detectBrand(host, queryBrand, storedBrand) {
  for (const [brand, needles] of Object.entries(BRAND_HOSTNAMES)) {
    if (needles.some((needle) => host.includes(needle))) {
      return brand;
    }
  }
  // Honor an explicit ?brand= / stored brand ONLY when recognized; unknown
  // values fall through to null (downstream `|| "default"` safely resolves it
  // to the default brand). Query takes precedence over the stored value,
  // preserving the prior precedence — but a junk query no longer shadows a
  // valid stored brand.
  if (isKnownBrand(queryBrand)) return queryBrand.toLowerCase();
  if (isKnownBrand(storedBrand)) return storedBrand.toLowerCase();
  return null;
}

// Reduce a hostname or full URL to its bare host: strips protocol, any path,
// trailing slashes, and port. "https://Staging.lucashealth.ai/" -> "staging.lucashealth.ai".
function normalizeHost(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split("/")[0]
    .split(":")[0]
    .replace(/\.$/, "");
}

// Resolve a brand from an AUTHENTICATED BRAIN URL (localStorage.hostName).
//
// Why this is separate from detectBrand: detectBrand keys off a host *needle*
// (the brand keyword appearing in the hostname). That works for tenants whose
// brain URL literally contains their brand (gtm.lucashealth.ai, shoreline…),
// but NOT for the orders brand, whose brain endpoint is the keyword-less
// `staging.lucashealth.ai` (REACT_APP_ENDPOINT_ORDERS). For that case we fall
// back to an EXACT match against the configured per-brand endpoints — the same
// map getBaseURL() in services/api.js uses to pick a brain. Keep the two in sync.
//
// Returns a recognized brand (e.g. "orders", "gtm") or null when none matches.
export function detectBrandFromBrainUrl(brainUrl) {
  const byNeedle = detectBrand(brainUrl || "", null, null);
  if (byNeedle) return byNeedle;

  const host = normalizeHost(brainUrl);
  if (!host) return null;

  // Mirror of getBaseURL()'s brand→endpoint map (services/api.js). The default
  // endpoint (REACT_APP_ENDPOINT) is intentionally excluded — it is not a brand.
  const brandEndpoints = {
    healcore: process.env.REACT_APP_ENDPOINT_HEALCORE,
    rpg: process.env.REACT_APP_ENDPOINT_RPG,
    shoreline: process.env.REACT_APP_ENDPOINT_SHORELINE,
    freehealthai: process.env.REACT_APP_ENDPOINT_FREEHEALTHAI,
    gtm: process.env.REACT_APP_ENDPOINT_GTM,
    [OPTIMAL_CARE_BRAND]: process.env.REACT_APP_ENDPOINT_OPTIMALCAREGRP,
    orders: process.env.REACT_APP_ENDPOINT_ORDERS,
    alphabiologix: process.env.REACT_APP_ENDPOINT_ALPHABIOLOGIX,
  };
  for (const brand of Object.keys(brandEndpoints)) {
    const endpoint = brandEndpoints[brand];
    if (endpoint && normalizeHost(endpoint) === host) return brand;
  }
  return null;
}
